/**
 * Right rail — the actor list plus properties and motion-path settings for the
 * selected aircraft.
 */
import { Copy, Plane, Route, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStudioStore, selectActiveScene, selectSelectedActor } from '../store/studioStore'
import type { CinematicPath } from '../types'
import { FIELD, LABEL, PANEL, SECTION_TITLE } from './studioStyles'

const NumberField = ({
  label,
  value,
  step = 1,
  min,
  max,
  suffix,
  onChange,
}: {
  label: string
  value: number
  step?: number
  min?: number
  max?: number
  suffix?: string
  onChange: (v: number) => void
}) => (
  <label className="block">
    <span className={LABEL}>
      {label}
      {suffix ? ` (${suffix})` : ''}
    </span>
    <input
      type="number"
      value={Number.isFinite(value) ? Math.round(value * 10) / 10 : 0}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(Number(e.target.value))}
      className={cn(FIELD, 'tabular-nums')}
    />
  </label>
)

export function ActorInspectorPanel({ onClose }: { onClose: () => void }) {
  const scene = useStudioStore(selectActiveScene)
  const actor = useStudioStore(selectSelectedActor)
  const tool = useStudioStore((s) => s.tool)

  const selectActor = useStudioStore((s) => s.selectActor)
  const updateAircraft = useStudioStore((s) => s.updateAircraft)
  const deleteAircraft = useStudioStore((s) => s.deleteAircraft)
  const duplicateAircraft = useStudioStore((s) => s.duplicateAircraft)
  const ensurePath = useStudioStore((s) => s.ensurePath)
  const clearPath = useStudioStore((s) => s.clearPath)
  const updatePath = useStudioStore((s) => s.updatePath)
  const setTool = useStudioStore((s) => s.setTool)

  const patchPath = (patch: Partial<CinematicPath>) => actor && updatePath(actor.id, patch)

  return (
    <div className={cn(PANEL, 'absolute right-4 top-16 bottom-24 flex w-[22rem] flex-col')}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <Plane className="h-4 w-4 text-sky-300" />
        <span className={SECTION_TITLE}>Scene actors</span>
        <div className="flex-1" />
        <span className="text-[10px] text-white/30">{scene?.aircraft.length ?? 0}</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
          title="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Actor list */}
      <div className="max-h-40 shrink-0 space-y-1 overflow-y-auto border-b border-white/10 px-3 py-2">
        {!scene?.aircraft.length && (
          <p className="py-3 text-center text-[11px] text-white/35">
            No aircraft yet. Open the Aircraft panel to place one.
          </p>
        )}
        {scene?.aircraft.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => selectActor(a.id)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md border px-2 py-1 text-left text-[11px]',
              a.id === actor?.id
                ? 'border-sky-400/40 bg-sky-500/20 text-sky-100'
                : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
            )}
          >
            <span className="min-w-0 flex-1 truncate">{a.name}</span>
            {a.path && <Route className="h-3 w-3 shrink-0 text-sky-300/70" />}
            <span className="shrink-0 text-[10px] text-white/30">{a.typeCode}</span>
          </button>
        ))}
      </div>

      {/* Properties */}
      {!actor ? (
        <p className="px-3 py-6 text-center text-[11px] text-white/35">
          Select an aircraft to edit it.
        </p>
      ) : (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
          <label className="block">
            <span className={LABEL}>Name / tail</span>
            <input
              value={actor.name}
              onChange={(e) => updateAircraft(actor.id, { name: e.target.value })}
              className={FIELD}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="X"
              suffix="ft"
              value={actor.position[0]}
              step={10}
              onChange={(v) => updateAircraft(actor.id, { position: [v, actor.position[1]] })}
            />
            <NumberField
              label="Z"
              suffix="ft"
              value={actor.position[1]}
              step={10}
              onChange={(v) => updateAircraft(actor.id, { position: [actor.position[0], v] })}
            />
            <NumberField
              label="Heading"
              suffix="0=N"
              value={actor.headingDeg}
              step={5}
              onChange={(v) => updateAircraft(actor.id, { headingDeg: ((v % 360) + 360) % 360 })}
            />
            <NumberField
              label="Altitude"
              suffix="ft"
              value={actor.altitudeFt}
              step={10}
              min={0}
              onChange={(v) => updateAircraft(actor.id, { altitudeFt: Math.max(0, v) })}
            />
            <NumberField
              label="Scale"
              value={actor.scale}
              step={0.1}
              min={0.05}
              max={20}
              onChange={(v) => updateAircraft(actor.id, { scale: Math.max(0.05, v) })}
            />
            <label className="block">
              <span className={LABEL}>Render</span>
              <select
                value={actor.renderMode}
                onChange={(e) =>
                  updateAircraft(actor.id, {
                    renderMode: e.target.value as typeof actor.renderMode,
                  })
                }
                className={FIELD}
              >
                <option value="solid" className="bg-slate-900">Solid</option>
                <option value="ghost_away" className="bg-slate-900">Ghost</option>
              </select>
            </label>
          </div>

          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => duplicateAircraft(actor.id)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-white/10
                         bg-white/5 py-1.5 text-[11px] text-white/70 hover:bg-white/10 hover:text-white"
            >
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </button>
            <button
              type="button"
              onClick={() => deleteAircraft(actor.id)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border
                         border-rose-400/30 bg-rose-500/10 py-1.5 text-[11px] text-rose-200
                         hover:bg-rose-500/20"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>

          {/* ── Motion path ───────────────────────────────────────────────── */}
          <div className="space-y-2 border-t border-white/10 pt-3">
            <div className="flex items-center gap-2">
              <span className={SECTION_TITLE}>Motion path</span>
              <div className="flex-1" />
              {actor.path ? (
                <button
                  type="button"
                  onClick={() => clearPath(actor.id)}
                  className="text-[10px] text-rose-300/70 hover:text-rose-200"
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    ensurePath(actor.id)
                    setTool('path')
                  }}
                  className="text-[10px] text-sky-300 hover:text-sky-200"
                >
                  Add path
                </button>
              )}
            </div>

            {!actor.path ? (
              <p className="text-[10px] leading-relaxed text-white/35">
                Without a path this aircraft sits still at its position above.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setTool(tool === 'path' ? 'select' : 'path')}
                  className={cn(
                    'w-full rounded-md border py-1.5 text-[11px] font-semibold',
                    tool === 'path'
                      ? 'border-sky-400/40 bg-sky-500/20 text-sky-100'
                      : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
                  )}
                >
                  {tool === 'path' ? 'Editing path — click map to add' : 'Edit path on map'}
                </button>

                <p className="text-[10px] text-white/35">
                  {actor.path.waypoints.length} waypoints · drag handles to move · Delete removes the
                  selected one
                </p>

                <label className="flex items-center gap-2 text-[11px] text-white/70">
                  <input
                    type="checkbox"
                    checked={actor.path.headingMode === 'tangent'}
                    onChange={(e) =>
                      patchPath({ headingMode: e.target.checked ? 'tangent' : 'fixed' })
                    }
                    className="h-3 w-3 accent-sky-400"
                  />
                  Autorotate (nose follows the path)
                </label>

                {actor.path.headingMode === 'fixed' && (
                  <NumberField
                    label="Fixed heading"
                    suffix="0=N"
                    value={actor.path.fixedHeadingDeg ?? actor.headingDeg}
                    step={5}
                    onChange={(v) => patchPath({ fixedHeadingDeg: ((v % 360) + 360) % 360 })}
                  />
                )}

                <label className="flex items-center gap-2 text-[11px] text-white/70">
                  <input
                    type="checkbox"
                    checked={actor.path.smooth}
                    onChange={(e) => patchPath({ smooth: e.target.checked })}
                    className="h-3 w-3 accent-sky-400"
                  />
                  Smooth corners
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <NumberField
                    label="Start"
                    suffix="s"
                    value={actor.path.startOffsetMs / 1000}
                    step={0.5}
                    min={0}
                    onChange={(v) => patchPath({ startOffsetMs: Math.max(0, v * 1000) })}
                  />
                  <NumberField
                    label="Duration"
                    suffix="s"
                    value={actor.path.durationMs / 1000}
                    step={0.5}
                    min={0.1}
                    onChange={(v) => patchPath({ durationMs: Math.max(100, v * 1000) })}
                  />
                  <label className="block">
                    <span className={LABEL}>Repeat</span>
                    <select
                      value={actor.path.playMode}
                      onChange={(e) =>
                        patchPath({ playMode: e.target.value as CinematicPath['playMode'] })
                      }
                      className={FIELD}
                    >
                      <option value="once" className="bg-slate-900">Once</option>
                      <option value="loop" className="bg-slate-900">Loop</option>
                      <option value="pingPong" className="bg-slate-900">Ping-pong</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className={LABEL}>Easing</span>
                    <select
                      value={actor.path.easing}
                      onChange={(e) =>
                        patchPath({ easing: e.target.value as CinematicPath['easing'] })
                      }
                      className={FIELD}
                    >
                      <option value="linear" className="bg-slate-900">Linear</option>
                      <option value="inOut" className="bg-slate-900">Ease in-out</option>
                      <option value="in" className="bg-slate-900">Ease in</option>
                      <option value="out" className="bg-slate-900">Ease out</option>
                    </select>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
